import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake?.vfs || pdfFonts.vfs || pdfFonts;

// The No-Due Certificate is rendered here, on the client, from the snapshot the
// backend FROZE onto the DischargeProcess row when the billing desk cleared the
// account (no_due_* fields). It reads nothing live.
//
// That is deliberate, and it is what makes a re-download trustworthy: the
// certificate states what was owed at the moment it was issued, so a charge
// posted afterwards cannot retroactively turn an already-issued certificate into
// a false statement. Anyone re-downloading it later gets the same document.
//
// (The backend has no PDF renderer — every PDF in this codebase is built
// client-side — so rendering here is also the only option that doesn't mean
// adding a PDF engine to Django.)

// A null amount means the balance could NOT be established (no billing account
// open, or the ledger was unreadable) — the backend deliberately stores NULL
// rather than 0 in that case. Rendering it as "INR 0.00" would turn "we don't
// know" into "they owe nothing", which is the one thing this document must never
// say falsely.
function formatAmount(value) {
  if (value == null) return "Not established";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `INR ${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

/**
 * Build and download the No-Due Certificate for a cleared discharge.
 *
 * @param dischargeProcess the DischargeProcess row, post-clearance (must carry
 *   no_due_certificate_number — the certificate does not exist before billing
 *   clearance issues it).
 * @param patient          { name, uhid } for the header.
 * @param facilityName     branding, if the caller has it.
 */
export function downloadNoDueCertificate(dischargeProcess, patient = {}, facilityName = "") {
  if (!dischargeProcess?.no_due_certificate_number) {
    throw new Error("This discharge has no No-Due Certificate — billing has not been cleared yet.");
  }

  const isOverride = dischargeProcess.no_due_status === "override";
  const amount = dischargeProcess.no_due_outstanding_amount;

  // An overridden certificate must not read like a clean one. It says, in plain
  // words, that a balance was outstanding and was cleared anyway — with the
  // reason. Printing "no dues" over an unpaid balance is the exact failure this
  // whole feature exists to prevent.
  const declaration = !isOverride
    ? "This is to certify that the above patient has NO DUES outstanding to this facility as at the date and time of clearance stated below, and that their account has been settled in full."
    : amount == null
    ? "This is to certify that the discharge of the above patient has been cleared by the billing department. The outstanding balance could NOT be established at the time of clearance, and the discharge was authorised for override as stated below. This certificate does NOT confirm that all dues have been settled."
    : `This is to certify that the discharge of the above patient has been cleared by the billing department. An outstanding balance of ${formatAmount(
        amount
      )} was recorded at the time of clearance and was authorised for override, as stated below. This certificate does NOT confirm that all dues have been settled.`;

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [48, 56, 48, 56],
    content: [
      facilityName
        ? { text: facilityName, style: "facility" }
        : null,
      {
        text: isOverride ? "DISCHARGE CLEARANCE CERTIFICATE" : "NO-DUE CERTIFICATE",
        style: "title",
      },
      {
        text: `Certificate No: ${dischargeProcess.no_due_certificate_number}`,
        style: "certNo",
      },
      { text: " ", margin: [0, 8] },
      {
        table: {
          widths: ["35%", "65%"],
          body: [
            ["Patient", patient.name || "—"],
            ["UHID", patient.uhid || "—"],
            ["Admission ID", String(dischargeProcess.admission_id ?? "—")],
            ["Outstanding at clearance", formatAmount(amount)],
            ["Cleared by", dischargeProcess.no_due_cleared_by_name || "—"],
            ["Cleared on", formatDateTime(dischargeProcess.no_due_generated_at)],
          ],
        },
        layout: "lightHorizontalLines",
        style: "detailTable",
      },
      { text: " ", margin: [0, 10] },
      { text: declaration, style: "declaration" },
      isOverride
        ? {
            style: "overrideBox",
            table: {
              widths: ["*"],
              body: [
                [
                  {
                    stack: [
                      { text: "REASON FOR OVERRIDE", style: "overrideLabel" },
                      { text: dischargeProcess.no_due_override_reason || "—", style: "overrideReason" },
                    ],
                  },
                ],
              ],
            },
            layout: "noBorders",
          }
        : null,
      { text: " ", margin: [0, 28] },
      {
        columns: [
          { text: "" },
          {
            width: "45%",
            stack: [
              { text: "_______________________", alignment: "center" },
              {
                text: dischargeProcess.no_due_cleared_by_name || "Billing Department",
                style: "signature",
              },
              { text: "Billing Department", style: "signatureSub" },
            ],
          },
        ],
      },
      {
        text: "This is a computer-generated certificate reflecting the account balance recorded at the time of clearance.",
        style: "footer",
      },
    ].filter(Boolean),
    styles: {
      facility: { fontSize: 13, bold: true, alignment: "center", margin: [0, 0, 0, 4] },
      title: { fontSize: 17, bold: true, alignment: "center", margin: [0, 6, 0, 2] },
      certNo: { fontSize: 10, alignment: "center", color: "#555555" },
      detailTable: { fontSize: 11 },
      declaration: { fontSize: 11, lineHeight: 1.4, alignment: "justify" },
      overrideBox: { margin: [0, 12, 0, 0] },
      overrideLabel: { fontSize: 9, bold: true, color: "#b91c1c", margin: [0, 0, 0, 3] },
      overrideReason: { fontSize: 11, italics: true },
      signature: { fontSize: 10, bold: true, alignment: "center", margin: [0, 4, 0, 0] },
      signatureSub: { fontSize: 9, color: "#555555", alignment: "center" },
      footer: { fontSize: 8, color: "#777777", alignment: "center", margin: [0, 30, 0, 0] },
    },
  };

  pdfMake
    .createPdf(docDefinition)
    .download(`${dischargeProcess.no_due_certificate_number}.pdf`);
}
